import { makeAutoObservable, runInAction } from "mobx";
import { logger } from "@/modules/logger";
import { audioRecordingModule } from "@/modules/audio-recording";
import { bytesToFloat32, computeRms } from "@/modules/audio-recording/waveform";
import { TranscriptionStatus } from "@/store/audio/recording/audio-recording.types";
import { WebMediaTranscriber } from "@/store/audio/recording/media-transcriber/web-media-transcriber";
import { WebMediaVisualizer } from "@/store/audio/recording/media-visualizer/web-media-visualizer";

export type FloatingAgentVoiceState = "idle" | "pending" | "starting" | "listening" | "finalizing";

/**
 * Instrumentation only (MEM-18817). A capture that outlives this window points
 * at a missed hold end; a stop() past it points at a wedged teardown. We log
 * but don't intervene — gathering intel on which path fires in the wild.
 */
const CAPTURE_STUCK_WARN_MS = 130_000;
const STOP_STUCK_WARN_MS = 5_000;

export type FloatingAgentVoiceFailure = "mic-denied" | "mic-unavailable";

type FloatingAgentVoiceStopOptions = {
  /** Runs after microphone audio through keyup can no longer enter the transcript. */
  onCaptureSealed?: () => void;
};

type FloatingAgentVoiceStartOptions = {
  /** Runs as soon as the local MediaRecorder owns an active microphone capture. */
  onCaptureStarted?: () => void;
};

/**
 * Outcome of one push-to-talk hold.
 *
 * - `transcript`: live transcription produced text — auto-send it.
 * - `audio-pending`: we captured audio but could not prove complete live
 *   transcription (offline, token failure, or the stream died mid-hold). The
 *   blob is replayed later through the realtime transcription pipeline.
 * - `empty`: nothing usable came out of the hold (no speech, or too short).
 */
export type FloatingAgentVoiceResult =
  | { kind: "transcript"; text: string }
  | { kind: "audio-pending"; blob: Blob; mimeType: string }
  | { kind: "empty" };

type ActiveCapture = {
  stream: MediaStream;
  recorder: MediaRecorder;
  chunks: Blob[];
  transcriber: WebMediaTranscriber | null;
  finalTranscripts: string[];
  latestPartial: string;
  visualizer: WebMediaVisualizer;
  amplitudeFrame: number | null;
  transcriptionFailed: boolean;
  transcriberWasLive: boolean;
  startedAtMs: number;
};

/** A hold at least this long with no transcript is worth instrumenting. */
const EMPTY_TRANSCRIPT_WARN_MIN_HOLD_MS = 3_000;

/**
 * One push-to-talk capture at a time: microphone in, three consumers out —
 * live transcription (AssemblyAI), a safety-net MediaRecorder blob, and an
 * amplitude probe for the listening UI. Recording never blocks on
 * connectivity; if live transcription can't run, the hold still records and
 * resolves as `audio-pending`.
 */
/**
 * Capture setup matching production voice mode (WebMediaController): 16kHz
 * at the source (the transcriber's audio graph runs at 16kHz — capturing at
 * the platform default forces an implicit resample) with explicit
 * echo-cancellation/noise-suppression/auto-gain so quiet or noisy mics reach
 * AssemblyAI normalized.
 */
const CAPTURE_SAMPLE_RATE_HZ = 16_000;
const CAPTURE_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    sampleRate: CAPTURE_SAMPLE_RATE_HZ,
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

export class FloatingAgentVoice {
  state: FloatingAgentVoiceState = "idle";
  /**
   * Voice level in [0, 1] (time-domain RMS × 4, hackweek's tuning), driven at
   * animation-frame rate while listening. Drives ring thickness/glow.
   */
  amplitude = 0;
  /** Five frequency-band levels in [0, 1] for the in-circle bars. */
  bars: number[] = [0, 0, 0, 0, 0];
  failure: FloatingAgentVoiceFailure | null = null;

  // Deliberately non-observable: MobX's deep conversion would copy the
  // chunks/transcript arrays, disconnecting them from the recorder and
  // transcriber closures that push into the originals.
  private capture: ActiveCapture | null = null;

  /**
   * Owns an in-flight microphone open.
   *
   * A quick tap can cancel one getUserMedia call and a second tap can start
   * another before the first promise settles. Identity, rather than `state`,
   * prevents that stale first result from taking over the newer session.
   */
  private startAttempt: symbol | null = null;

  // Instrumentation only: warns if a capture outlives CAPTURE_STUCK_WARN_MS
  // (likely a missed hold end). Does not tear anything down.
  private captureStuckTimer: ReturnType<typeof setTimeout> | null = null;

  /** User-dictionary term boosting, same source production voice mode uses. */
  private readonly getCustomVocabulary: () => string[];

  constructor({ getCustomVocabulary = () => [] }: { getCustomVocabulary?: () => string[] } = {}) {
    this.getCustomVocabulary = getCustomVocabulary;
    makeAutoObservable<FloatingAgentVoice, "capture" | "getCustomVocabulary" | "startAttempt">(
      this,
      {
        capture: false,
        getCustomVocabulary: false,
        startAttempt: false,
      }
    );
  }

  get isListening(): boolean {
    return this.state === "listening";
  }

  /**
   * Shows immediate gesture feedback without touching the microphone. A
   * confirmed hold or second tap promotes this state through start().
   */
  beginPending(): void {
    if (this.state !== "idle") return;
    this.failure = null;
    this.state = "pending";
  }

  async start({ onCaptureStarted }: FloatingAgentVoiceStartOptions = {}): Promise<
    | { ok: true }
    | { ok: false; canceled: true }
    | { ok: false; canceled?: undefined; failure: FloatingAgentVoiceFailure }
  > {
    if (this.capture) {
      // A prior hold's capture is still live at the start of a new hold — its
      // stop() never ran, so this and every later start() no-op here and PTT
      // looks dead (MEM-18817). Log to see how often this is the cause.
      logger.warn({
        message:
          "[FloatingAgentVoice] start() with a capture already live — likely a missed hold end",
        info: { state: this.state },
      });
      return { ok: true };
    }
    // A duplicate keydown while the mic is opening must not open a second
    // stream; the in-flight call owns the hold.
    if (this.state === "starting") return { ok: false, canceled: true };

    const startAttempt = Symbol("floating-agent-voice-start");
    this.startAttempt = startAttempt;
    this.failure = null;
    // Flips synchronously so the UI reacts on keydown, before the mic-open
    // latency below.
    this.state = "starting";

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(CAPTURE_CONSTRAINTS);
    } catch (error) {
      if (this.startAttempt !== startAttempt) {
        return { ok: false, canceled: true };
      }

      const failure: FloatingAgentVoiceFailure =
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "mic-denied"
          : "mic-unavailable";
      logger.warn({
        message: "[FloatingAgentVoice] getUserMedia failed",
        info: { error: String(error), failure },
      });
      runInAction(() => {
        this.startAttempt = null;
        this.failure = failure;
        this.state = "idle";
      });
      return { ok: false, failure };
    }

    // The hold ended (or teardown ran) while the mic was opening — starting
    // now would orphan a recorder nothing ever stops.
    if (this.startAttempt !== startAttempt) {
      for (const track of stream.getTracks()) track.stop();
      return { ok: false, canceled: true };
    }

    const chunks: Blob[] = [];
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream);
      recorder.ondataavailable = event => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.start();
    } catch (error) {
      for (const track of stream.getTracks()) track.stop();
      logger.warn({
        message: "[FloatingAgentVoice] MediaRecorder failed to start",
        info: { error: String(error) },
      });
      runInAction(() => {
        this.startAttempt = null;
        this.failure = "mic-unavailable";
        this.state = "idle";
      });
      return { ok: false, failure: "mic-unavailable" };
    }

    const visualizer = new WebMediaVisualizer();

    const capture: ActiveCapture = {
      stream,
      recorder,
      chunks,
      transcriber: null,
      finalTranscripts: [],
      latestPartial: "",
      visualizer,
      amplitudeFrame: null,
      transcriptionFailed: false,
      transcriberWasLive: false,
      startedAtMs: performance.now(),
    };
    this.startAttempt = null;
    this.capture = capture;
    this.armCaptureStuckTimer(capture);
    onCaptureStarted?.();

    void this.startTranscriber(capture);

    // Keep the dots at rest until at least one complete capture path is live.
    // Normally that is the local PCM worklet; if it cannot open, the safety
    // recorder is already running and remains the ground truth.
    try {
      await capture.transcriber?.waitForAudioCaptureReady();
    } catch (_error) {
      // The safety recorder remains active and will be used as the fallback.
    }

    if (this.capture !== capture) {
      return { ok: false, canceled: true };
    }

    runInAction(() => {
      this.state = "listening";
    });
    void this.startAmplitudeLoop(capture);

    return { ok: true };
  }

  /**
   * Discards pending feedback or an active capture synchronously.
   *
   * Shortcut-combo recognition must make the next keydown eligible
   * immediately. Network and visualizer cleanup can finish in the background
   * because any recorder and stream are sealed here.
   */
  cancel(): void {
    this.startAttempt = null;

    const capture = this.capture;
    this.capture = null;
    this.clearCaptureStuckTimer();

    if (capture) {
      if (capture.amplitudeFrame !== null) {
        cancelAnimationFrame(capture.amplitudeFrame);
      }

      capture.recorder.ondataavailable = null;
      capture.recorder.onstop = null;
      if (capture.recorder.state !== "inactive") {
        try {
          capture.recorder.stop();
        } catch (error) {
          logger.warn({
            message: "[FloatingAgentVoice] MediaRecorder failed to cancel",
            info: { error: String(error) },
          });
        }
      }

      capture.transcriber?.dispose();
      void capture.visualizer.stop().catch(() => undefined);
      for (const track of capture.stream.getTracks()) track.stop();
    }

    runInAction(() => {
      this.state = "idle";
      this.amplitude = 0;
      this.bars = [0, 0, 0, 0, 0];
    });
  }

  /**
   * Ends the hold and resolves its outcome. Transcript wins when live
   * transcription produced anything. The recorded blob is handed back only
   * when complete live transcription could not be proven.
   */
  async stop({
    onCaptureSealed,
  }: FloatingAgentVoiceStopOptions = {}): Promise<FloatingAgentVoiceResult> {
    const capture = this.capture;
    if (!capture) {
      // Released while the mic was still opening: back to idle, and the
      // pending start() sees its ownership invalidated and cancels itself.
      if (this.state === "starting") {
        this.startAttempt = null;
        this.state = "idle";
      } else if (this.state === "pending") {
        this.state = "idle";
      }
      return { kind: "empty" };
    }
    this.capture = null;
    this.clearCaptureStuckTimer();

    runInAction(() => {
      this.state = "finalizing";
    });

    // Instrumentation only: if teardown stalls past the threshold, log it — a
    // wedged recorder/transcriber leaves state at "finalizing" (MEM-18817).
    const stopStuckTimer = setTimeout(() => {
      logger.warn({
        message:
          "[FloatingAgentVoice] stop() still finalizing past threshold — possible wedged teardown",
        info: { state: this.state, thresholdMs: STOP_STUCK_WARN_MS },
      });
    }, STOP_STUCK_WARN_MS);

    if (capture.amplitudeFrame !== null) cancelAnimationFrame(capture.amplitudeFrame);

    // Seal both ground-truth boundaries from keyup, independently of token
    // fetch / websocket setup. MediaRecorder stops synchronously at this call;
    // the worklet acknowledges its microphone-to-silence transition.
    const transcriber = capture.transcriber;
    const blobPromise = this.stopRecorder(capture);
    const sealPromise = transcriber
      ? this.sealTranscriptInput(capture, transcriber)
      : Promise.resolve();

    const blob = await blobPromise;
    await sealPromise;
    onCaptureSealed?.();

    // Flush the sealed local buffer and wait for the provider's final-turn
    // event. stop() then waits for the server's Termination acknowledgment
    // before the transcript is read.
    if (transcriber && !capture.transcriptionFailed) {
      try {
        await transcriber.finalizePendingAudio();
      } catch (_error) {
        // Without a proven final flush, the complete recorder blob wins.
        capture.transcriptionFailed = true;
      }
    }

    if (transcriber) {
      try {
        await transcriber.stop();
      } catch (error) {
        capture.transcriptionFailed = true;
        logger.warn({
          message: "[FloatingAgentVoice] transcriber stop failed",
          info: { error: String(error) },
        });
      } finally {
        transcriber.dispose();
      }
    }

    try {
      await capture.visualizer.stop();
    } catch (_error) {
      // Visualizer teardown is best-effort.
    }
    for (const track of capture.stream.getTracks()) track.stop();

    clearTimeout(stopStuckTimer);

    if (this.capture === null && this.startAttempt === null) {
      runInAction(() => {
        this.state = "idle";
        this.amplitude = 0;
        this.bars = [0, 0, 0, 0, 0];
      });
    }

    const transcript = [...capture.finalTranscripts, capture.latestPartial]
      .map(text => text.trim())
      .filter(Boolean)
      .join(" ");

    // A failed/interrupted stream may have dropped words the recorder still
    // has. Likewise, a remaining partial means the final-turn event plus
    // Termination did not prove the last turn was finalized. In either case
    // the complete recording outranks the possibly holey live text. A session
    // that never became live also cannot use an absent transcript as evidence
    // of silence.
    const hasUnfinalizedTurn = capture.latestPartial.trim().length > 0;
    const hasRecordedAudio = blob != null && blob.size > 0;
    if (
      hasRecordedAudio &&
      (capture.transcriptionFailed || !capture.transcriberWasLive || hasUnfinalizedTurn)
    ) {
      return { kind: "audio-pending", blob, mimeType: blob.type || "audio/webm" };
    }

    if (transcript.length > 0) {
      return { kind: "transcript", text: transcript };
    }

    // No transcript from a live, finalized stream — treated as silence and
    // discarded. Instrumentation only (MEM-19159): a deliberate hold landing
    // here would mean the transcriber heard silence while the recorder
    // captured audio (the two consume separate audio graphs). Fleet volume
    // of this log decides whether the recording should be kept instead.
    const heldMs = performance.now() - capture.startedAtMs;
    if (hasRecordedAudio && heldMs >= EMPTY_TRANSCRIPT_WARN_MIN_HOLD_MS) {
      logger.warn({
        message:
          "[FloatingAgentVoice] live transcript empty for a deliberate hold — discarding recording",
        info: { heldMs: Math.round(heldMs), blobSize: blob.size },
      });
    }

    return { kind: "empty" };
  }

  private armCaptureStuckTimer(capture: ActiveCapture): void {
    this.clearCaptureStuckTimer();
    this.captureStuckTimer = setTimeout(() => {
      // Only the still-active capture: a completed stop() nulled `capture` and
      // cleared this timer, so reaching here means the hold's end never came.
      if (this.capture !== capture) return;
      logger.warn({
        message:
          "[FloatingAgentVoice] capture still live past threshold — likely a missed hold end (not torn down)",
        info: { state: this.state, thresholdMs: CAPTURE_STUCK_WARN_MS },
      });
    }, CAPTURE_STUCK_WARN_MS);
  }

  private clearCaptureStuckTimer(): void {
    if (this.captureStuckTimer === null) return;
    clearTimeout(this.captureStuckTimer);
    this.captureStuckTimer = null;
  }

  /**
   * Seals microphone input at keyup without waiting on transcription I/O.
   *
   * The worklet's acknowledgment is ordered after its preceding microphone
   * messages. It then emits silence at the audio graph's native cadence while
   * the provider finalizes the last word.
   */
  private async sealTranscriptInput(
    capture: ActiveCapture,
    transcriber: WebMediaTranscriber
  ): Promise<void> {
    try {
      await transcriber.sealAudioInput();
    } catch (_error) {
      // Without the worklet acknowledgment, live delivery is not provably
      // complete. The recorder blob remains authoritative.
      capture.transcriptionFailed = true;
    }
  }

  private stopRecorder(capture: ActiveCapture): Promise<Blob | null> {
    const capturedBlob = () =>
      capture.chunks.length > 0
        ? new Blob(capture.chunks, { type: capture.recorder.mimeType })
        : null;

    return new Promise(resolve => {
      if (capture.recorder.state === "inactive") {
        resolve(capturedBlob());
        return;
      }
      capture.recorder.onstop = () => {
        resolve(capturedBlob());
      };
      try {
        capture.recorder.stop();
      } catch (error) {
        logger.warn({
          message: "[FloatingAgentVoice] MediaRecorder failed to stop",
          info: { error: String(error) },
        });
        resolve(capturedBlob());
      }
    });
  }

  private async startTranscriber(capture: ActiveCapture): Promise<void> {
    const transcriber = new WebMediaTranscriber({
      tokenProvider: audioRecordingModule.assemblyAiTokenProvider,
      stream: capture.stream,
      customVocabulary: this.getCustomVocabulary(),
      requireCompleteFinalization: true,
    });

    transcriber.setEventHandlers({
      onTranscriptFinal: ({ finalTurn }) => {
        capture.finalTranscripts.push(finalTurn.transcript);
        capture.latestPartial = "";
      },
      onTranscriptPartial: ({ partialTurn }) => {
        capture.latestPartial = partialTurn.transcript;
      },
      onError: ({ error }) => {
        capture.transcriptionFailed = true;
        logger.warn({
          message: "[FloatingAgentVoice] live transcription error",
          info: { error: String(error) },
        });
      },
      onStatusChange: ({ status }) => {
        if (status === TranscriptionStatus.Live) {
          capture.transcriberWasLive = true;
          return;
        }

        // Initial connection retries are lossless because PCM remains in the
        // local buffer. Once a socket has been live, a disconnect creates an
        // unknowable delivery gap, so the continuous recorder becomes
        // authoritative.
        if (capture.transcriberWasLive) {
          capture.transcriptionFailed = true;
        }
      },
    });

    capture.transcriber = transcriber;

    try {
      await transcriber.start();
      capture.transcriberWasLive = true;
    } catch (error) {
      // Offline or token failure — the hold keeps recording; the blob is
      // replayed later through the realtime transcription pipeline.
      logger.info({
        message: "[FloatingAgentVoice] live transcription unavailable — recording continues",
        info: { error: String(error) },
      });
      capture.transcriptionFailed = true;
      capture.transcriber = null;
      transcriber.dispose();
    }
  }

  private async startAmplitudeLoop(capture: ActiveCapture): Promise<void> {
    try {
      const { analyser, visualizer } = await capture.visualizer.start({
        stream: capture.stream,
        sampleRate: CAPTURE_SAMPLE_RATE_HZ,
      });
      const timeBuffer = new Uint8Array(visualizer.fftSize);
      const freqBuffer = new Uint8Array(analyser.frequencyBinCount);
      const bandCount = 5;
      const bandSize = Math.max(1, Math.floor(freqBuffer.length / bandCount));

      const tick = () => {
        if (this.capture !== capture) return;

        visualizer.getByteTimeDomainData(timeBuffer);
        const level = Math.min(1, computeRms(bytesToFloat32(timeBuffer)) * 4);

        analyser.getByteFrequencyData(freqBuffer);
        const bars = Array.from({ length: bandCount }, (_, band) => {
          const start = band * bandSize;
          let sum = 0;
          for (let i = start; i < start + bandSize; i += 1) sum += freqBuffer[i] ?? 0;
          return sum / bandSize / 255;
        });

        runInAction(() => {
          this.amplitude = level;
          this.bars = bars;
        });
        capture.amplitudeFrame = requestAnimationFrame(tick);
      };
      capture.amplitudeFrame = requestAnimationFrame(tick);
    } catch (_error) {
      // No amplitude UI is acceptable; the capture itself is unaffected.
    }
  }
}
